import type { HelpAnchorId } from '@myrewards/contracts';
import { useSyncExternalStore, type ReactElement } from 'react';
import { cx } from './cx';
import { StatusPill } from './StatusPill';
import s from './HelpShown.module.css';

/*
 * "Show me" from Help (APP-090). A thing Help can point at carries `data-help-anchor="<id>"` (the
 * ids are the server's registry, `HelpAnchorId`). When Help shows it, the shell scrolls to it and
 * moves focus there, then marks it shown here: the thing renders `data-help-shown` (the ring, in
 * the global CSS) and a "Shown from Help" tag, so the highlight is never colour alone. It stays
 * until focus leaves it, another thing is shown, or the page changes.
 *
 * The one mechanism (APP-090 §8) is the attribute and the registry: `useHelpAnchor(id)` gives an
 * element its attributes and whether it's shown, and `HelpShownTag` goes inside it. Card does
 * this for its `helpAnchor` prop; anything that isn't a card (an export link, a filter bar) calls
 * the hook itself.
 */

let shownId: HelpAnchorId | null = null;
const listeners = new Set<() => void>();

/** Marks one anchor shown (or none). The shell calls it; pages only read it. */
export function setHelpShown(id: HelpAnchorId | null): void {
  if (shownId === id) return;
  shownId = id;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True while Help is showing this anchor. */
function useHelpShown(anchor: HelpAnchorId | undefined): boolean {
  return useSyncExternalStore(
    subscribe,
    () => anchor !== undefined && shownId === anchor,
    () => false,
  );
}

/** What an element Help can point at carries. */
export interface HelpAnchorProps {
  'data-help-anchor'?: HelpAnchorId;
  'data-help-shown'?: 'true';
}

/**
 * Makes an element something Help's "Show me" can point at: spread `props` on it and put
 * `<HelpShownTag shown={shown} />` inside it. No id, no attributes.
 */
export function useHelpAnchor(id: HelpAnchorId | undefined): {
  props: HelpAnchorProps;
  shown: boolean;
} {
  const shown = useHelpShown(id);
  if (id === undefined) return { props: {}, shown: false };
  return {
    props: { 'data-help-anchor': id, ...(shown && { 'data-help-shown': 'true' as const }) },
    shown,
  };
}

/**
 * The "Shown from Help" tag. `inline` sits in a heading row; `edge` sits on the top edge of a
 * box that has no heading row (its parent is positioned and doesn't clip).
 */
export function HelpShownTag({
  shown,
  placement = 'inline',
}: {
  shown: boolean;
  placement?: 'inline' | 'edge';
}): ReactElement | null {
  if (!shown) return null;
  return (
    <StatusPill
      tone="info"
      size="sm"
      label="Shown from Help"
      className={cx(s.tag, placement === 'edge' && s.edge)}
    />
  );
}
